import { TableCell } from '@material-ui/core';

import { useRouteRef } from '@backstage/core-plugin-api';

import { LabelChips, useLabelFilters } from '@spotify/confidence-common-react';
import {
  extractLastNameComponent,
  extractWorkflowFromInstanceName,
  formatDatePast,
  useThrottledPrefetch,
} from '@spotify/confidence-core-react';
import {
  ReanalysisDataExposureState,
  ReanalysisDataMetricDataState,
  ReanalysisListItemFragment,
  getTypeOrNull,
} from '@spotify/confidence-graphql';
import {
  WorkflowInstanceTableRow,
  workflowInstanceRouteRef,
} from '@spotify/plugin-confidence-workflows';
import {
  NameCell,
  OwnerCell,
  StateCell,
  UpdateCell,
} from '@spotify/plugin-confidence-workflows-react';

import { useReanalysisPrefetch } from '../../hooks';
import { tabForState } from '../ReanalysisWorkflowInstancePage/utils';
import { ProgressCell } from './ProgressCell';

export function ReanalysisTableRow({
  workflowInstance,
}: {
  workflowInstance: ReanalysisListItemFragment;
}) {
  // Prefetching the workflow instance when hovering over the row, so that
  // when the user clicks on the row, the workflow instance is already loaded.
  const prefetch = useReanalysisPrefetch();
  const { onMouseEnter, onMouseLeave } = useThrottledPrefetch(() =>
    prefetch(workflowInstance.name),
  );

  const tab = tabForState(workflowInstance);
  const { addLabelFilter, labelDefinitions } = useLabelFilters();
  const hasLabels = labelDefinitions.length > 0;

  const workflowInstanceRoute = useRouteRef(workflowInstanceRouteRef);
  const href = workflowInstanceRoute({
    workflow: extractWorkflowFromInstanceName(workflowInstance.name)!,
    id: extractLastNameComponent(workflowInstance.name)!,
  });

  // If any metric or exposure calculation fails, the row should be in error state
  const error =
    workflowInstance.moduleData2?.reanalysis?.metrics?.some(
      m => m.state === ReanalysisDataMetricDataState.StateFailed,
    ) ||
    workflowInstance.moduleData2?.reanalysis?.exposureState ===
      ReanalysisDataExposureState.StateFailed;

  return (
    <WorkflowInstanceTableRow
      workflowInstance={workflowInstance}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      transform={path => `${path}/${tab}`}
    >
      <NameCell
        kind="Analysis"
        displayName={workflowInstance.displayName}
        comments={getTypeOrNull(workflowInstance.comments)}
        secondaryText={`Created ${formatDatePast(
          new Date(workflowInstance.createTime),
        )}`}
        href={href}
      />
      <StateCell state="live" label="Active" error={error} />
      <ProgressCell workflowInstance={workflowInstance} />
      <UpdateCell updateTime={workflowInstance.updateTime} />
      <OwnerCell owner={getTypeOrNull(workflowInstance.owner)} />
      {hasLabels && (
        <TableCell>
          <LabelChips
            labels={workflowInstance.labels}
            labelDefinitions={labelDefinitions}
            onLabelClick={addLabelFilter}
          />
        </TableCell>
      )}
    </WorkflowInstanceTableRow>
  );
}
